/**
 * 问卷 schema_json 与表单状态类型（对齐历史 Vue 设计；不含抽奖相关字段）
 */

/** UI 问卷类型；提交时映射为后端 `scene`（survey / evaluation / quiz / vote） */
export type QuestionnaireType = 'survey' | 'evaluation' | 'quiz' | 'exam' | 'assessment' | 'vote';

export type QuestionType =
  | 'single_choice'
  | 'multiple_choice'
  | 'text'
  | 'number'
  | 'rating'
  | 'true_false'
  | 'sorting'
  | 'matrix'
  | 'date'
  | 'file_upload'
  | 'poll';

export type SubmitterInfoFieldType = 'text' | 'tel' | 'email' | 'select' | 'radio' | 'date' | 'number';

export interface SubmitterInfoField {
  field: string;
  label: string;
  type: SubmitterInfoFieldType;
  required?: boolean;
  placeholder?: string;
  pattern?: string;
  options?: Array<{ label: string; value: string }>;
  min?: number;
  max?: number;
}

export interface QuestionnaireSettings {
  collectSubmitterInfo: boolean;
  submitterInfoFields: SubmitterInfoField[];
  allowAnonymous: boolean;
  timeLimit: number | null;
  showProgress: boolean;
  randomizeQuestions: boolean;
  showScore: boolean;
  passingScore: number | null;
}

export interface QuestionOption {
  id: string;
  label: string;
  value: string;
}

export interface QuestionBase {
  id: string;
  type: QuestionType;
  title: string;
  description?: string;
  required: boolean;
  score?: number;
  correctAnswer?: unknown;
  correctAnswers?: unknown[];
  explanation?: string;
  order?: number;
}

export interface SingleChoiceQuestion extends QuestionBase {
  type: 'single_choice';
  options: QuestionOption[];
}

export interface MultipleChoiceQuestion extends QuestionBase {
  type: 'multiple_choice';
  options: QuestionOption[];
  minSelections?: number;
  maxSelections?: number;
}

export interface TextQuestion extends QuestionBase {
  type: 'text';
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
}

export interface NumberQuestion extends QuestionBase {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
}

export type Question = SingleChoiceQuestion | MultipleChoiceQuestion | TextQuestion | NumberQuestion | QuestionBase;

export interface Section {
  type: 'section';
  id: string;
  name: string;
  description?: string;
  questions: Question[];
}

/** 持久化到 schema_json 的根结构 */
export interface QuestionnaireSchema {
  version: string;
  settings: QuestionnaireSettings;
  sections: Array<Section | Question>;
}

export interface QuestionnaireFormData {
  title: string;
  description?: string;
  type: QuestionnaireType;
  isShared: boolean;
  useSections: boolean;
  sections: Section[];
  questions: Question[];
  settings: QuestionnaireSettings;
}

export function createDefaultSettings(): QuestionnaireSettings {
  return {
    collectSubmitterInfo: false,
    submitterInfoFields: [],
    allowAnonymous: false,
    timeLimit: null,
    showProgress: true,
    randomizeQuestions: false,
    showScore: true,
    passingScore: null,
  };
}

export function createEmptyForm(): QuestionnaireFormData {
  return {
    title: '',
    description: '',
    type: 'survey',
    isShared: false,
    useSections: false,
    sections: [],
    questions: [],
    settings: createDefaultSettings(),
  };
}
